import { useEffect, useState } from "react";
import type { EntityAnalytics } from "../../../worker/services/analytics";
import { api } from "../../lib/api";
import { Spinner, Empty } from "../../components/States";
import { fmtRating } from "../../lib/format";

// Read entirely from existing rows — no counters are written on the request
// path, so nothing here can slow a review submission down.

export function Analytics() {
  const [items, setItems] = useState<EntityAnalytics[] | null>(null);

  useEffect(() => {
    api.dashAnalytics().then((r) => setItems(r.items)).catch(() => setItems([]));
  }, []);

  if (!items) return <Spinner />;
  if (items.length === 0) {
    return <Empty title="Nothing to report yet" hint="Claim a listing to see its analytics." />;
  }

  const peak = Math.max(1, ...items.flatMap((e) => e.trend.map((t) => t.count)));

  return (
    <div className="narrow-lg">
      <h2>Analytics</h2>
      {items.map((e) => (
        <section key={e.entityId} className="card p-16 mb-16">
          <h3 className="m-0">{e.name}</h3>

          <div className="score-band mt-12">
            <div className="score-cell">
              <div className="label">Weighted rating</div>
              <div className="big rating-num">{fmtRating(e.ratingWeighted)}</div>
            </div>
            <div className="score-cell">
              <div className="label">Reviews</div>
              <div className="big rating-num">{e.reviewCount}</div>
            </div>
            <div className="score-cell">
              <div className="label">Verified</div>
              <div className="big rating-num">{e.verifiedCount}</div>
            </div>
            <div className="score-cell">
              <div className="label">Helpful votes</div>
              <div className="big rating-num">{e.helpfulVotes}</div>
            </div>
          </div>

          <h4 className="section">Review volume by month</h4>
          {e.trend.length === 0 ? (
            <p className="muted">No published reviews yet.</p>
          ) : (
            <div className="grid gap-6">
              {e.trend.map((t) => (
                <div key={t.month} className="row gap-8 items-center">
                  <span className="mono fs-sm trend-month">{t.month}</span>
                  {/* SVG geometry, so a data-driven width needs no inline style. */}
                  <svg className="bar flex-1" viewBox="0 0 100 6" preserveAspectRatio="none" role="img"
                       aria-label={`${t.count} reviews in ${t.month}`}>
                    <rect className="bar-track" x="0" y="0" width="100" height="6" rx="3" />
                    <rect className="bar-fill" x="0" y="0" width={(t.count / peak) * 100} height="6" rx="3" />
                  </svg>
                  <span className="mono fs-sm">{t.count}</span>
                  <span className="muted fs-sm">avg {fmtRating(t.avgRating)}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
